// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, AlertCircle, RefreshCw, ArrowLeft, Merge, Phone, Mail, Building2, TrendingUp } from 'lucide-react'
import CustomerMergeModal from '../components/CustomerMergeModal'
import { useApi } from '../hooks/useApi'

/**
 * 重複顧客一覧ページ
 * 類似度の高い顧客ペアを表示し、マージ機能を提供
 */
const DuplicateCustomersPage = () => {
  const navigate = useNavigate()
  const api = useApi()
  const [duplicates, setDuplicates] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [minSimilarity, setMinSimilarity] = useState(0.7)
  const [selectedPair, setSelectedPair] = useState(null)
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false)

  // 重複顧客を取得
  const fetchDuplicates = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await api.get(`/api/customers/duplicates?minSimilarity=${minSimilarity}`)
      setDuplicates(data.duplicates || [])
    } catch (err) {
      console.error('重複顧客取得エラー:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchDuplicates()
  }, [minSimilarity])

  // マージ実行
  const handleMerge = async (primaryId, secondaryId, mergeStrategy) => {
    try {
      await api.post('/api/customers/merge', {
        primaryId,
        secondaryId,
        mergeStrategy,
      })

      // マージ成功後、リストを再取得
      await fetchDuplicates()
      setIsMergeModalOpen(false)
      setSelectedPair(null)
    } catch (err) {
      console.error('マージエラー:', err)
      throw err
    }
  }

  // マージモーダルを開く
  const openMergeModal = (pair) => {
    setSelectedPair(pair)
    setIsMergeModalOpen(true)
  }

  // 類似度バッジの色を取得
  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    if (similarity >= 0.8) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
    if (similarity >= 0.7) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
    return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
  }

  // 一致理由のアイコンを取得
  const getReasonIcon = (reason) => {
    if (reason.includes('電話番号')) return <Phone className="w-4 h-4" />
    if (reason.includes('メール')) return <Mail className="w-4 h-4" />
    if (reason.includes('名前') || reason.includes('会社名')) return <Building2 className="w-4 h-4" />
    return <TrendingUp className="w-4 h-4" />
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* ヘッダー */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/customers')}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Users className="w-7 h-7" />
                  重複顧客の検出
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  類似度の高い顧客レコードを検出し、マージすることができます
                </p>
              </div>
            </div>
            <button
              onClick={fetchDuplicates}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              再検出
            </button>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* フィルター */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              最小類似度:
            </label>
            <input
              type="range"
              min="0.5"
              max="1.0"
              step="0.05"
              value={minSimilarity}
              onChange={(e) => setMinSimilarity(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {(minSimilarity * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">エラー</h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* ローディング */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        )}

        {/* 重複なし */}
        {!isLoading && !error && duplicates.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              重複顧客が見つかりませんでした
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              類似度 {(minSimilarity * 100).toFixed(0)}% 以上の重複顧客は存在しません
            </p>
          </div>
        )}

        {/* 重複顧客リスト */}
        {!isLoading && !error && duplicates.length > 0 && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {duplicates.length}件の重複候補が見つかりました
            </div>
            {duplicates.map((pair, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* 類似度ヘッダー */}
                <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSimilarityColor(pair.similarity)}`}>
                        類似度: {(pair.similarity * 100).toFixed(1)}%
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {pair.matchReasons.map((reason, idx) => (
                          <span
                            key={idx}
                            className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          >
                            {getReasonIcon(reason)}
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => openMergeModal(pair)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                    >
                      <Merge className="w-4 h-4" />
                      マージ
                    </button>
                  </div>
                </div>

                {/* 顧客情報比較 */}
                <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
                  {/* 顧客1 */}
                  <div className="p-6">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">顧客 1</div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      {pair.customer1.name || '名前未設定'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      {pair.customer1.companyName && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Building2 className="w-4 h-4" />
                          {pair.customer1.companyName}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Phone className="w-4 h-4" />
                        {pair.customer1.phoneNumber}
                      </div>
                      {pair.customer1.email && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Mail className="w-4 h-4" />
                          {pair.customer1.email}
                        </div>
                      )}
                      <div className="text-gray-500 dark:text-gray-400 mt-3">
                        通話回数: {pair.customer1.callCount || 0}回 / 
                        総通話時間: {Math.floor((pair.customer1.totalCallDuration || 0) / 60)}分
                      </div>
                    </div>
                  </div>

                  {/* 顧客2 */}
                  <div className="p-6">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">顧客 2</div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      {pair.customer2.name || '名前未設定'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      {pair.customer2.companyName && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Building2 className="w-4 h-4" />
                          {pair.customer2.companyName}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Phone className="w-4 h-4" />
                        {pair.customer2.phoneNumber}
                      </div>
                      {pair.customer2.email && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Mail className="w-4 h-4" />
                          {pair.customer2.email}
                        </div>
                      )}
                      <div className="text-gray-500 dark:text-gray-400 mt-3">
                        通話回数: {pair.customer2.callCount || 0}回 / 
                        総通話時間: {Math.floor((pair.customer2.totalCallDuration || 0) / 60)}分
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* マージモーダル */}
      {selectedPair && (
        <CustomerMergeModal
          isOpen={isMergeModalOpen}
          onClose={() => {
            setIsMergeModalOpen(false)
            setSelectedPair(null)
          }}
          primaryCustomer={selectedPair.customer1}
          secondaryCustomer={selectedPair.customer2}
          onMerge={handleMerge}
        />
      )}
    </div>
  )
}

export default DuplicateCustomersPage
